package com.vamvisions.main.pages.contact

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.vamvisions.main.components.Card
import com.vamvisions.main.components.EmailLinkPill
import com.vamvisions.main.components.GitHubIcon
import com.vamvisions.main.components.InstagramIcon
import com.vamvisions.main.components.LinkPill
import com.vamvisions.main.components.MailIcon
import com.vamvisions.main.contact.domain.model.ContactTopic
import com.vamvisions.main.contact.presentation.ContactAction
import com.vamvisions.main.contact.presentation.ContactResult
import com.vamvisions.main.contact.presentation.ContactState
import com.vamvisions.main.contact.presentation.ContactViewModel
import com.vamvisions.main.core.presentation.i18n.Strings
import com.vamvisions.main.core.presentation.i18n.UiText
import com.vamvisions.main.core.presentation.i18n.resolve
import com.vamvisions.main.di.koinViewModel
import com.vamvisions.main.i18n.LocalStrings
import com.vamvisions.main.layout.PageChrome
import com.vamvisions.main.layout.PageLayout
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H1
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLElement

const val OWNER_EMAIL = "anamaria.vilcu@vamvisions.com"
const val OWNER_INSTAGRAM = "https://www.instagram.com/vilcu.anamaria.96"
const val OWNER_GITHUB = "https://github.com/AnnaMGh"

private const val PHOTO_PATH = "/contact/profile.webp"

private const val FORM_TITLE_ID = "contact-form-title"
private const val REACH_TITLE_ID = "contact-reach-title"
private const val TOPIC_LABEL_ID = "contact-topic-label"
private const val NAME_ID = "contact-name"
private const val EMAIL_ID = "contact-email"
private const val MESSAGE_ID = "contact-message"
private const val WEBSITE_ID = "contact-website"

@Composable
fun ContactRoot(chrome: PageChrome) {
    val viewModel = koinViewModel<ContactViewModel>()
    val state by viewModel.state.collectAsState()
    PageLayout(title = LocalStrings.current.contactTitle, chrome = chrome, panel = false) {
        ContactScreen(state = state, onAction = viewModel::onAction)
    }
}

/** Holds the form's input elements, so Send can move focus to the first invalid one. */
private class FieldElements {
    var name: HTMLElement? = null
    var email: HTMLElement? = null
    var message: HTMLElement? = null
}

@Composable
fun ContactScreen(state: ContactState, onAction: (ContactAction) -> Unit) {
    val strings = LocalStrings.current
    val fields = remember { FieldElements() }
    LaunchedEffect(state.invalidSendCount) {
        if (state.invalidSendCount == 0) return@LaunchedEffect
        when {
            state.nameError != null -> fields.name
            state.emailError != null -> fields.email
            else -> fields.message
        }?.focus()
    }

    Div(attrs = { classes(ContactStyles.page) }) {
        Div(attrs = { classes(ContactStyles.intro) }) {
            H1(attrs = { classes(ContactStyles.introTitle) }) { Text(strings.contactIntroTitle) }
            P(attrs = { classes(ContactStyles.introText) }) { Text(strings.contactIntroText) }
        }
        // The DOM order is the reading order and the single-column order: photo, form, direct links. On wide screens
        // the grid places the links under the photo.
        Div(attrs = { classes(ContactStyles.grid) }) {
            Div(attrs = { classes(ContactStyles.photoFrame) }) {
                Img(src = PHOTO_PATH, alt = strings.contactPhotoAlt, attrs = {
                    classes(ContactStyles.photo)
                    attr("width", "900")
                    attr("height", "1200")
                })
            }
            Card(glass = true, attrs = {
                classes(ContactStyles.formCard)
                attr("aria-labelledby", FORM_TITLE_ID)
            }) {
                H2(attrs = {
                    id(FORM_TITLE_ID)
                    classes(ContactStyles.cardTitle)
                }) { Text(strings.contactFormTitle) }
                ContactForm(state, onAction, fields)
            }
            ReachCard()
        }
    }
}

@Composable
private fun ContactForm(state: ContactState, onAction: (ContactAction) -> Unit, fields: FieldElements) {
    val strings = LocalStrings.current
    Form(attrs = {
        classes(ContactStyles.form)
        // The ViewModel validates with the server's rules and shows localized errors instead of the browser's.
        attr("novalidate", "")
        onSubmit { event ->
            event.preventDefault()
            onAction(ContactAction.Send)
        }
    }) {
        TopicChoice(state.topic) { onAction(ContactAction.TopicClicked(it)) }
        Div(attrs = { classes(ContactStyles.fieldRow) }) {
            Field(NAME_ID, strings.contactNameLabel, state.nameError) {
                Input(type = InputType.Text) {
                    fieldAttrs(NAME_ID, strings.contactNamePlaceholder, state.nameError) { fields.name = it }
                    attr("autocomplete", "name")
                    value(state.name)
                    onInput { onAction(ContactAction.NameChanged(it.value)) }
                }
            }
            Field(EMAIL_ID, strings.contactEmailLabel, state.emailError) {
                Input(type = InputType.Email) {
                    fieldAttrs(EMAIL_ID, strings.contactEmailPlaceholder, state.emailError) { fields.email = it }
                    attr("autocomplete", "email")
                    attr("aria-required", "true")
                    value(state.email)
                    onInput { onAction(ContactAction.EmailChanged(it.value)) }
                }
            }
        }
        Field(MESSAGE_ID, strings.contactMessageLabel, state.messageError) {
            TextArea(value = state.message) {
                fieldAttrs(MESSAGE_ID, strings.contactMessagePlaceholder, state.messageError) { fields.message = it }
                classes(ContactStyles.textArea)
                attr("aria-required", "true")
                onInput { onAction(ContactAction.MessageChanged(it.value)) }
            }
        }
        Honeypot(state.website) { onAction(ContactAction.WebsiteChanged(it)) }
        Button(attrs = {
            type(ButtonType.Submit)
            classes(ContactStyles.sendButton)
            if (state.isSending) disabled()
        }) {
            Text(if (state.isSending) strings.contactSending else strings.contactSend)
            Span(attrs = { attr("aria-hidden", "true") }) { Text("→") }
        }
        // Always present, so screen readers announce the result when it appears.
        P(attrs = {
            classes(ContactStyles.result)
            when (state.result) {
                ContactResult.Sent -> classes(ContactStyles.resultSuccess)
                is ContactResult.Failed -> classes(ContactStyles.resultFailure)
                null -> Unit
            }
            attr("role", "status")
        }) {
            when (val result = state.result) {
                ContactResult.Sent -> {
                    Span(attrs = { attr("aria-hidden", "true") }) { Text("✓ ") }
                    Text(strings.contactSent)
                }
                is ContactResult.Failed -> Text(result.error.resolve(strings))
                null -> Unit
            }
        }
    }
}

/** Holds the topic chips, so the arrow keys can move focus between them. */
private class ChipElements {
    val chips = arrayOfNulls<HTMLElement>(ContactTopic.entries.size)
}

/**
 * The optional "What's it about?" choice: a radio group of chips that can be cleared, which native radios can't. The
 * group is one Tab stop: the focused chip while focus is inside, otherwise the selected one, else the first. The arrow
 * keys move focus; Space or Enter, the button's own activation keys, select or clear the focused chip.
 */
@Composable
private fun TopicChoice(selected: ContactTopic?, onClick: (ContactTopic) -> Unit) {
    val strings = LocalStrings.current
    val elements = remember { ChipElements() }
    var focusedIndex by remember { mutableStateOf<Int?>(null) }
    val topics = ContactTopic.entries
    val tabbableIndex = focusedIndex ?: selected?.ordinal ?: 0

    Div(attrs = { classes(ContactStyles.topic) }) {
        P(attrs = {
            id(TOPIC_LABEL_ID)
            classes(ContactStyles.fieldLabel)
        }) {
            Text(strings.contactTopicLabel + " ")
            Span(attrs = { classes(ContactStyles.optionalHint) }) { Text(strings.contactOptional) }
        }
        Div(attrs = {
            classes(ContactStyles.chips)
            attr("role", "radiogroup")
            attr("aria-labelledby", TOPIC_LABEL_ID)
            // Leaving the group, the tab stop returns to the selected chip, or the first.
            onFocusOut { event ->
                val next = event.relatedTarget
                if (next == null || elements.chips.none { it == next }) focusedIndex = null
            }
        }) {
            topics.forEachIndexed { index, topic ->
                val checked = topic == selected
                Button(attrs = {
                    // Not a submit button, although it sits in the form.
                    type(ButtonType.Button)
                    classes(ContactStyles.chip)
                    attr("role", "radio")
                    attr("aria-checked", checked.toString())
                    attr("tabindex", if (index == tabbableIndex) "0" else "-1")
                    ref { element ->
                        elements.chips[index] = element
                        onDispose { elements.chips[index] = null }
                    }
                    onFocus { focusedIndex = index }
                    onClick { onClick(topic) }
                    onKeyDown { event ->
                        val next = when (event.key) {
                            "ArrowRight", "ArrowDown" -> (index + 1) % topics.size
                            "ArrowLeft", "ArrowUp" -> (index - 1 + topics.size) % topics.size
                            "Home" -> 0
                            "End" -> topics.lastIndex
                            else -> return@onKeyDown
                        }
                        event.preventDefault()
                        elements.chips[next]?.focus()
                    }
                }) {
                    if (checked) Span(attrs = {
                        classes(ContactStyles.chipCheck)
                        attr("aria-hidden", "true")
                    }) { Text("✓") }
                    Text(topic.label(strings))
                }
            }
        }
    }
}

private fun ContactTopic.label(strings: Strings) = when (this) {
    ContactTopic.APP_DEVELOPMENT -> strings.contactTopicAppDevelopment
    ContactTopic.DIGITAL_PRODUCT -> strings.contactTopicDigitalProduct
    ContactTopic.PARTNERSHIP -> strings.contactTopicPartnership
    ContactTopic.OTHER -> strings.contactTopicOther
}

/** The "Reach me directly" card: the email address, GitHub and Instagram as link pills. */
@Composable
private fun ReachCard() {
    Card(glass = true, attrs = {
        classes(ContactStyles.reachCard)
        attr("aria-labelledby", REACH_TITLE_ID)
    }) {
        H2(attrs = {
            id(REACH_TITLE_ID)
            classes(ContactStyles.reachTitle)
        }) { Text(LocalStrings.current.contactReachTitle) }
        Div(attrs = { classes(ContactStyles.pills) }) {
            EmailLinkPill(OWNER_EMAIL) { MailIcon() }
            LinkPill("GitHub", OWNER_GITHUB) { GitHubIcon() }
            LinkPill("Instagram", OWNER_INSTAGRAM) { InstagramIcon() }
        }
    }
}

/** A labelled form field with its error, which describes the input while it is shown. */
@Composable
private fun Field(id: String, label: String, error: UiText?, input: @Composable () -> Unit) {
    Div(attrs = { classes(ContactStyles.field) }) {
        Label(forId = id, attrs = { classes(ContactStyles.fieldLabel) }) { Text(label) }
        input()
        if (error != null) {
            P(attrs = {
                id(errorId(id))
                classes(ContactStyles.fieldError)
            }) { Text(error.resolve(LocalStrings.current)) }
        }
    }
}

private fun AttrsScope<out HTMLElement>.fieldAttrs(id: String, placeholder: String, error: UiText?, onElement: (HTMLElement?) -> Unit) {
    id(id)
    classes(ContactStyles.textInput)
    attr("placeholder", placeholder)
    attr("aria-invalid", (error != null).toString())
    if (error != null) attr("aria-describedby", errorId(id))
    ref { element ->
        onElement(element)
        onDispose { onElement(null) }
    }
}

private fun errorId(fieldId: String) = "$fieldId-error"

/** The field bots fill in and people never see: hidden from view, from keyboard focus and from assistive technology. */
@Composable
private fun Honeypot(value: String, onChange: (String) -> Unit) {
    Div(attrs = {
        classes(ContactStyles.honeypot)
        attr("aria-hidden", "true")
    }) {
        Label(forId = WEBSITE_ID) { Text("Website") }
        Input(type = InputType.Text) {
            id(WEBSITE_ID)
            attr("name", "website")
            attr("tabindex", "-1")
            attr("autocomplete", "off")
            value(value)
            onInput { onChange(it.value) }
        }
    }
}
